
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Lock, 
  Users, 
  FileText, 
  Calendar, 
  Smartphone, 
  FileCheck, 
  AlertTriangle, 
  Percent, 
  MessageSquare, 
  Thermometer, 
  Palette 
} from "lucide-react";
import { useFeatureItems } from "@/hooks/useFeatureItems";

interface HeaderProps {
  hideNavigation?: boolean;
  hideSignIn?: boolean;
}

const Header = ({ hideNavigation = false, hideSignIn = false }: HeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: featureItems = [] } = useFeatureItems();

  // Icon mapping for dynamic features
  const iconMap: Record<string, any> = {
    Lock,
    Users,
    FileText,
    Calendar,
    Smartphone,
    FileCheck,
    AlertTriangle,
    Percent,
    MessageSquare,
    Thermometer,
    Palette,
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img 
                src="/lovable-uploads/c9d4d449-ac7c-4868-af9e-bf9b48c9faad.png" 
                alt="WellSuites" 
                className="h-6 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          {!hideNavigation && (
            <nav className="hidden md:flex space-x-8">
              <DropdownMenu>
                <DropdownMenuTrigger className="text-gray-700 hover:text-blue-600 transition-colors flex items-center gap-1">
                  Features
                  <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white border border-gray-200 shadow-lg min-w-[250px] z-50">
                  {featureItems.map((item) => {
                    const Icon = iconMap[item.icon_name] || Lock;
                    return (
                      <DropdownMenuItem key={item.id} asChild>
                        {item.href !== "#" ? (
                          <Link 
                            to={item.href} 
                            className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          >
                            <div className="bg-gray-100 p-2 rounded-lg">
                              <Icon className="h-4 w-4 text-gray-600" />
                            </div>
                            <span className="font-medium">{item.label}</span>
                          </Link>
                        ) : (
                          <div className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer">
                            <div className="bg-gray-100 p-2 rounded-lg">
                              <Icon className="h-4 w-4 text-gray-600" />
                            </div>
                            <span className="font-medium">{item.label}</span>
                          </div>
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
              <a href="#solutions" className="text-gray-700 hover:text-blue-600 transition-colors">
                Solutions
              </a>
              {/* Pricing link removed - page is hidden */}
              <Link to="/contact" className="text-gray-700 hover:text-blue-600 transition-colors">
                Contact
              </Link>
            </nav>
          )}

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {!hideSignIn && (
              <Link to="/login">
                <Button variant="ghost" className="text-gray-700 hover:text-blue-600">
                  Sign in
                </Button>
              </Link>
            )}
            <Link to="/login">
              <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
                Beta Login
              </Button>
            </Link>
          </div>

          {/* Mobile Auth Button - Always show when navigation is hidden */}
          {hideNavigation && (
            <div className="md:hidden">
              <Link to="/login">
                <Button size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
                  Login
                </Button>
              </Link>
            </div>
          )}

          {/* Mobile menu button */}
          {!hideNavigation && (
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          )}
        </div>

        {/* Mobile Navigation */}
        {!hideNavigation && isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200">
              <div className="space-y-1">
                <div className="text-gray-700 font-medium px-3 py-2">Features</div>
                {featureItems.map((item) => {
                  const Icon = iconMap[item.icon_name] || Lock;
                  return (
                    <div key={item.id}>
                      {item.href !== "#" ? (
                        <Link
                          to={item.href}
                          className="flex items-center gap-3 px-6 py-3 text-gray-600 hover:text-blue-600 transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="bg-gray-100 p-1.5 rounded">
                            <Icon className="h-3 w-3 text-gray-600" />
                          </div>
                          <span className="text-sm">{item.label}</span>
                        </Link>
                      ) : (
                        <div
                          className="flex items-center gap-3 px-6 py-3 text-gray-600 hover:text-blue-600 transition-colors cursor-pointer"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="bg-gray-100 p-1.5 rounded">
                            <Icon className="h-3 w-3 text-gray-600" />
                          </div>
                          <span className="text-sm">{item.label}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <a
                href="#solutions"
                className="block px-3 py-2 text-gray-700 hover:text-blue-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Solutions
              </a>
              {/* Pricing link removed - page is hidden */}
              <Link
                to="/contact"
                className="block px-3 py-2 text-gray-700 hover:text-blue-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              <div className="pt-4 border-t border-gray-200">
                {!hideSignIn && (
                  <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-blue-600 mb-2">
                      Sign in
                    </Button>
                  </Link>
                )}
                <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
                    Beta login
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
